import React, { useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

/**
 * Unregisters `names` whenever the fields go from shown to hidden.
 *
 * The shared <Form/> runs react-hook-form with its default `shouldUnregister: false`, so a
 * field that is conditionally rendered keeps its value in the form state after it disappears.
 * That value still travels in the payload — a number input the user cleared before switching
 * modes arrives as "" and fails `nullable|numeric` on a field nobody can see to fix.
 *
 * Mirrors what the app already does in expo-app SectionField.
 */
export const useUnregisterWhenHidden = (hidden: boolean, names: string[] = []) => {
    const { unregister } = useFormContext();
    const wasHidden = useRef(hidden);
    // names are usually an inline literal; key on the content so the effect isn't re-run per render
    const key = names.join("|");

    useEffect(() => {
        if (hidden && !wasHidden.current && names.length > 0) {
            // The default survives: only the current value must stop travelling in the
            // payload. Dropping the default too meant a user who switched a mode back and
            // forth got empty fields and then saved the stored configuration away as null.
            unregister(names, { keepDefaultValue: true });
        }

        wasHidden.current = hidden;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hidden, key, unregister]);
};

interface Props {
    name: string;
    condition: (value: any) => boolean;
    /** Field names to drop from the form state once the condition turns false. */
    unregisterOnHide?: string[];
    children: React.ReactNode;
}

export const ConditionalFormPart: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
    name,
    condition,
    unregisterOnHide,
    children
}: Props) => {
    // useWatch, not watch(): watch() only re-renders the owner of useForm(), and React bails out of the children
    const value = useWatch({ name });
    const isShown = useMemo(() => condition(value), [condition, value]);

    useUnregisterWhenHidden(!isShown, unregisterOnHide);

    return <>{isShown ? children : null}</>;
};

export default ConditionalFormPart;
